import { ToastController } from '@grofers/dock-design-kit'
import { EMAIL_REGEX } from '../constants/commonConstants'
import {
  BLINKIT_DEEPLINK_DESTINATIONS,
  BLINKIT_DEEPLINK_SCHEME,
  BLINKIT_LINK_SCHEMES,
  BLINKIT_SHORT_LINK_HOSTS,
  BLINKIT_WEB_HOSTS,
  NEW_CAMPAIGN_CONSTANTS,
} from '../constants/newCampaignConstants'

export const getQueryParamValue = (
  searchParams: URLSearchParams,
  queryParam: string
): string | null => {
  if (!searchParams) {
    return null
  }
  return searchParams.get(queryParam)
}

export const validateEmail = (emailValue: string): boolean => {
  const trimmed = emailValue.trim()
  if (!trimmed) {
    return false
  }
  return EMAIL_REGEX.test(trimmed)
}

// Mirrors the backend's normalizeTargetURL so the field shows the message the API would return.
// Returns the error, or the link in the form it was checked in (https:// added, scheme and host
// lowercased), which is what gets sent so the API receives exactly what the field accepted.
const checkBlinkitLink = (value: string): { error: string } | { link: string } => {
  const { ERRORS } = NEW_CAMPAIGN_CONSTANTS.BLINKIT_LINK_FIELD
  let link = value.trim()
  if (!link) return { error: ERRORS.REQUIRED }
  if (/\s/.test(link)) return { error: ERRORS.INVALID }
  // Only a scheme this field accepts counts as one; "blinkit.com/x", "//blinkit.com/x" or a link
  // whose query carries another URL get https://, and anything else then fails the host check.
  if (!BLINKIT_LINK_SCHEMES.includes(link.split(':')[0].toLowerCase())) {
    link = `https://${link.replace(/^\/\//, '')}`
  }

  // Read the authority from the raw text: URL() drops a default :443, which must still be rejected.
  const match = /^([^:/?#]+):\/\/([^/?#]*)/.exec(link)
  if (!match || !match[2] || /[@:]/.test(match[2])) return { error: ERRORS.INVALID }
  const scheme = match[1].toLowerCase()
  const host = match[2].toLowerCase()
  const normalized = `${scheme}://${host}${link.slice(match[0].length)}`

  if (scheme === BLINKIT_DEEPLINK_SCHEME) {
    return (BLINKIT_DEEPLINK_DESTINATIONS as readonly string[]).includes(host)
      ? { link: normalized }
      : { error: ERRORS.DEEPLINK_HOST }
  }
  if (BLINKIT_SHORT_LINK_HOSTS.includes(host)) return { error: ERRORS.SHORT_LINK }
  if (!BLINKIT_WEB_HOSTS.includes(host)) return { error: ERRORS.NOT_BLINKIT }
  try {
    new URL(normalized)
    return { link: normalized }
  } catch {
    return { error: ERRORS.INVALID }
  }
}

export const getBlinkitLinkError = (value: string): string | null => {
  const result = checkBlinkitLink(value)
  return 'error' in result ? result.error : null
}

export const isValidBlinkitLink = (value: string): boolean => getBlinkitLinkError(value) === null

// The accepted link in the form it was validated in, or undefined when it is not valid.
export const normalizeBlinkitLink = (value: string): string | undefined => {
  const result = checkBlinkitLink(value)
  return 'link' in result ? result.link : undefined
}

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} kb`
  return `${(kb / 1024).toFixed(1)} MB`
}

export const formatCompactINR = (value: number): string => {
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (absValue >= 1_00_00_000) {
    return `${sign}₹${(absValue / 1_00_00_000).toFixed(1)}Cr`
  }
  if (absValue >= 1_00_000) {
    return `${sign}₹${(absValue / 1_00_000).toFixed(1)}L`
  }
  if (absValue >= 1_000) {
    return `${sign}₹${(absValue / 1_000).toFixed(1)}K`
  }
  return `${sign}₹${absValue}`
}

export const debounce = (callback: (...args: unknown[]) => void, delay = 300) => {
  let timerId: ReturnType<typeof setTimeout> | null = null

  const cancel = () => {
    if (timerId !== null) {
      clearTimeout(timerId)
      timerId = null
    }
  }

  const trigger = (...args: unknown[]) => {
    cancel()
    timerId = setTimeout(() => {
      callback(...args)
    }, delay)
  }

  return { trigger, cancel }
}

export const downloadCsv = (content: string, fileName: string) => {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/csv' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}

// Shows an error toast with the API message, or the fallback when there is none.
// A brand the user can't open comes back as 403 (no access) or 404 (no such brand).
export const isBrandAccessDenied = (error: unknown) => {
  const status = (error as { status?: string } | null)?.status
  return status === '403' || status === '404'
}

// Retries once like the app default, but not when the answer is "no access".
export const retryUnlessAccessDenied = (failureCount: number, error: unknown) =>
  !isBrandAccessDenied(error) && failureCount < 1

export const notifyError = (error: unknown, fallbackMessage: string) => {
  ToastController.notify({
    hierarchy: 'low',
    headlineContent: '',
    paragraphContent: (error as { message?: string } | null)?.message || fallbackMessage,
    variant: 'error',
    iconName: 'info',
  })
}
